/**
 * LDS / FlightsHotel order list cards: pick a human-facing reference for "מס׳ הזמנה".
 * Pure display — does not mutate orders or affect booking / payment flows.
 */

import { isLikelyOpaqueLdsDealToken } from "./resolveCustomerFacingOfferNumber.js";

export const LDS_ORDER_DISPLAY_REF_PENDING = "ממתין לאישור ספק";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** @param {unknown} v */
function trimStr(v) {
  if (v == null) return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(v).trim();
  if (typeof v === "string") return v.trim();
  return "";
}

function firstTrimmed(...vals) {
  for (const v of vals) {
    const t = trimStr(v);
    if (t) return t;
  }
  return "";
}

/**
 * Values that must never be shown as the order reference on cards.
 * @param {string} s
 */
function isForbiddenDisplayRef(s) {
  const t = trimStr(s);
  if (!t) return true;
  if (t.startsWith("eyJ")) return true;
  if (UUID_RE.test(t)) return true;
  if (isLikelyOpaqueLdsDealToken(t)) return true;
  if (t.length > 64) return true;
  return false;
}

/**
 * proposalNumber / similar — only when short and looks like a real reference, not a blob.
 * @param {string} s
 */
function isReadableShortProposal(s) {
  const t = trimStr(s);
  if (t.length < 3 || t.length > 24) return false;
  if (!/^[A-Za-z0-9\-_/]+$/.test(t)) return false;
  return true;
}

/** @param {Record<string, unknown>|null|undefined} order */
function parseOrderResponseObject(order) {
  const or = order?.order_response;
  if (or == null) return null;
  if (typeof or === "object" && !Array.isArray(or)) return /** @type {Record<string, unknown>} */ (or);
  if (typeof or === "string") {
    const t = or.trim();
    if (t.startsWith("{")) {
      try {
        const p = JSON.parse(t);
        return p && typeof p === "object" && !Array.isArray(p)
          ? /** @type {Record<string, unknown>} */ (p)
          : null;
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * Optional console trace for debugging display source.
 * Enabled in Vite dev or when `VITE_LDS_ORDER_DISPLAY_REF_TRACE=1`.
 */
function traceLdsOrderDisplayRef(payload) {
  let enabled = false;
  try {
    enabled = Boolean(
      import.meta.env?.DEV || import.meta.env?.VITE_LDS_ORDER_DISPLAY_REF_TRACE === "1"
    );
  } catch {
    enabled = false;
  }
  if (!enabled) return;
  if (import.meta.env?.MODE === "test") return;
  // eslint-disable-next-line no-console
  console.info("[LDS_ORDER_DISPLAY_REF_TRACE]", payload);
}

/**
 * Public short label for badges (ellipsis), never applied to Hebrew pending copy.
 * @param {string} displayValue
 */
export function shortenLdsOrderDisplayRef(displayValue) {
  const t = trimStr(displayValue);
  if (!t || t === LDS_ORDER_DISPLAY_REF_PENDING) return t;
  if (t.length <= 14) return t;
  return `${t.slice(0, 8)}…`;
}

/**
 * Resolve the string to show for LDS-like order rows on cards / collapsed details.
 *
 * Hierarchy:
 * - order.booking_ref
 * - order.bookingRef
 * - order.supplierReservationNumber
 * - order.reservationNumber
 * - parsed order_response: supplierReservationNumber, reservationNumber
 * - reservation.supplierPNR / bookingRef inside parsed order_response (common LDS)
 * - proposalNumber only if short/readable
 * - else LDS_ORDER_DISPLAY_REF_PENDING
 *
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {{ displayValue: string, selectedSource: string }}
 */
export function resolveLdsOrderDisplayRef(order) {
  const o = order && typeof order === "object" ? /** @type {Record<string, unknown>} */ (order) : {};

  const tryPair = (raw, source) => {
    const t = trimStr(raw);
    if (!t || isForbiddenDisplayRef(t)) return null;
    return { displayValue: t, selectedSource: source };
  };

  let picked =
    tryPair(o.booking_ref, "order.booking_ref") ||
    tryPair(o.bookingRef, "order.bookingRef") ||
    tryPair(
      firstTrimmed(o.supplierReservationNumber, o.supplier_reservation_number),
      "order.supplierReservationNumber"
    ) ||
    tryPair(
      firstTrimmed(o.reservationNumber, o.reservation_number),
      "order.reservationNumber"
    );

  if (!picked) {
    const parsed = parseOrderResponseObject(o);
    if (parsed) {
      picked =
        tryPair(
          firstTrimmed(
            parsed.supplierReservationNumber,
            parsed.supplier_reservation_number,
            parsed.SupplierReservationNumber
          ),
          "order_response.supplierReservationNumber"
        ) ||
        tryPair(
          firstTrimmed(
            parsed.reservationNumber,
            parsed.reservation_number,
            parsed.ReservationNumber
          ),
          "order_response.reservationNumber"
        );

      if (!picked) {
        const resv = parsed.reservation;
        if (resv && typeof resv === "object" && !Array.isArray(resv)) {
          const rv = /** @type {Record<string, unknown>} */ (resv);
          picked =
            tryPair(
              firstTrimmed(
                rv.supplierReservationNumber,
                rv.supplier_reservation_number,
                rv.supplierPNR,
                rv.bookingRef,
                rv.booking_ref
              ),
              "order_response.reservation.supplierRef"
            );
        }
      }

      if (!picked) {
        const prop = firstTrimmed(parsed.proposalNumber, parsed.proposal_number);
        if (prop && !isForbiddenDisplayRef(prop) && isReadableShortProposal(prop)) {
          picked = { displayValue: prop, selectedSource: "order_response.proposalNumber" };
        }
      }
    }
  }

  if (!picked) {
    picked = {
      displayValue: LDS_ORDER_DISPLAY_REF_PENDING,
      selectedSource: "fallback.pending",
    };
  }

  traceLdsOrderDisplayRef({
    order_id: o.id ?? o.orderid ?? o.orderId,
    booking_ref: o.booking_ref ?? o.bookingRef,
    selected_source: picked.selectedSource,
    display_value: picked.displayValue,
  });

  return picked;
}
